import { cn } from '../../lib/format.js';

/**
 * The signature device (PRD §6.3).
 *
 * A monospace horizontal data rail carrying exactly three attributes drawn from
 * the product's spec template. It appears on every product card and at the top
 * of every product page, identical in both worlds:
 *
 *   EAU DE PARFUM · 100 ML · AMBER-WOOD
 *   RUBBER-COMPOSITE · 340 G · INTERMEDIATE
 *
 * Same grammar, different vocabulary. This is the element that makes two
 * catalogues read as one house rather than two stores in a trenchcoat, and it is
 * genuinely useful — it front-loads the comparison a buyer makes anyway.
 *
 * A product without three specifications renders nothing at all. A rail with a
 * gap in it reads as broken; its absence reads as a product still being
 * catalogued, which is the truthful signal.
 */

const sizes = {
  sm: 'text-2xs gap-x-1.5',
  md: 'text-2xs gap-x-2',
  lg: 'text-xs gap-x-3',
};

export const SpecStrip = ({ items, size = 'sm', className, muted = true, as: Component = 'p' }) => {
  if (!items?.length || items.length < 3) return null;

  const values = items.slice(0, 3);

  return (
    <Component
      className={cn(
        'flex flex-wrap items-center font-mono tracking-[0.11em] uppercase',
        muted ? 'text-muted' : 'text-ink',
        sizes[size],
        className,
      )}
      // The separators are decoration; a screen reader should read three values,
      // not "middot".
      aria-label={`Specification: ${values.join(', ')}`}
    >
      {values.map((value, index) => (
        <span key={`${value}-${index}`} className="flex items-center">
          {index > 0 && (
            <span aria-hidden="true" className="mr-1.5 text-mist select-none sm:mr-2">
              ·
            </span>
          )}
          <span className="truncate">{value}</span>
        </span>
      ))}
    </Component>
  );
};

export default SpecStrip;
